// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { GameState, GameConfig, Position, Letter, GameAction } from '../types/game';
import { validateWord, calculateScoreBreakdown } from './wordValidator';
import { findAllWords, findFirstWords, WordPath } from './wordFinder';
import { getWordsByLength } from './wordDictionary';
import { IS_DEBUG } from './env';

// Level-based grid generation - much faster and more reliable!

export const generateGrid = (levelId: number, gridSizeOverride?: number): Letter[][] => {
  // If gridSizeOverride is explicitly provided (including 0), use it
  // Otherwise, derive size from level
  const size = gridSizeOverride !== undefined ? gridSizeOverride : getLevelInfo(levelId).size;
  
  // Debug logging to verify grid size override is working
  if (IS_DEBUG && gridSizeOverride !== undefined) {
    console.log(`[generateGrid] Override provided: gridSize=${gridSizeOverride}, level=${levelId}, using size=${size}`);
  }
  
  return generateProceduralGrid(size);
};

// Get level information
export const getLevelInfo = (levelId: number) => {
  // Procedural levels: derive basic info from levelId
  // Grid size increases every 3 levels: 3x3 (1-3), 4x4 (4-6), 5x5 (7-9), 6x6 (10+)
  // Max size is 6x6 to prevent overflow issues
  let size = 3;
  if (levelId <= 3) size = 3;
  else if (levelId <= 6) size = 4;
  else if (levelId <= 9) size = 5;
  else size = 6; // Level 10+ uses 6x6 (max)
  
  const difficulty = levelId <= 5 ? 'easy' : levelId <= 12 ? 'medium' : 'hard';
  return { id: levelId, size, difficulty } as any;
};

// Lightweight check: Are there enough unused letters to potentially form a word?
// This is a fast heuristic before doing the expensive findAllWords check
export const hasPotentialMoves = (grid: Letter[][]): boolean => {
  if (grid.length === 0) return false;
  const unusedLetters = grid.flat().filter(letter => !letter.used);
  // Need at least 3 letters to form a valid word
  return unusedLetters.length >= 3;
};

// Check if there are any remaining valid words in the grid
export const hasRemainingWords = (grid: Letter[][], foundWords: string[]): boolean => {
  // Quick heuristic check first - if not enough letters, no moves possible
  if (!hasPotentialMoves(grid)) {
    return false;
  }
  
  // Expensive check - only run if heuristic passes
  const allPossibleWords = findAllWords(grid);
  const remainingWords = allPossibleWords.filter(wordPath => 
    !foundWords.includes(wordPath.word)
  );
  return remainingWords.length > 0;
};

// Get count of remaining words
export const getRemainingWordCount = (grid: Letter[][], foundWords: string[]): number => {
  const allPossibleWords = findAllWords(grid);
  const remainingWords = allPossibleWords.filter(wordPath => 
    !foundWords.includes(wordPath.word)
  );
  return remainingWords.length;
};

export const getAdjacentPositions = (position: Position, gridSize: number): Position[] => {
  const { row, col } = position;
  const adjacent: Position[] = [];
  
  // Check all 8 directions (including diagonals)
  for (let dr = -1; dr <= 1; dr++) {
    for (let dc = -1; dc <= 1; dc++) {
      if (dr === 0 && dc === 0) continue; // Skip the current position
      
      const newRow = row + dr;
      const newCol = col + dc;
      
      if (newRow >= 0 && newRow < gridSize && newCol >= 0 && newCol < gridSize) {
        adjacent.push({ row: newRow, col: newCol });
      }
    }
  }
  
  return adjacent;
};

export const isAdjacent = (pos1: Position, pos2: Position): boolean => {
  const rowDiff = Math.abs(pos1.row - pos2.row);
  const colDiff = Math.abs(pos1.col - pos2.col);
  return rowDiff <= 1 && colDiff <= 1 && !(rowDiff === 0 && colDiff === 0);
};

export const canSelectPosition = (
  position: Position, 
  selectedPath: Position[], 
  grid: Letter[][]
): boolean => {
  const { row, col } = position;
  
  // Check if position is within bounds
  if (row < 0 || row >= grid.length || col < 0 || col >= grid[0].length) {
    return false;
  }
  
  // Check if letter is already used
  if (grid[row][col].used) {
    return false;
  }
  
  // Check if position is already selected
  if (selectedPath.some(p => p.row === row && p.col === col)) {
    return false;
  }
  
  // If this is the first selection, allow it
  if (selectedPath.length === 0) {
    return true;
  }
  
  // Check if position is adjacent to the last selected position
  const lastPosition = selectedPath[selectedPath.length - 1];
  return isAdjacent(position, lastPosition);
};

export const getCurrentWord = (selectedPath: Position[], grid: Letter[][]): string => {
  return selectedPath
    .map(pos => grid[pos.row][pos.col].char)
    .join('');
};

export const isGameComplete = (grid: Letter[][]): boolean => {
  // Game is complete if grid is empty
  if (grid.length === 0) {
    return true;
  }
  
  // Check if there are enough unused letters to form at least one 3-letter word
  const unusedLetters = grid.flat().filter(letter => !letter.used);
  return unusedLetters.length < 3;
};

// Apply gravity: make letters fall down within columns (keep original grid size, show empty tiles)
export const applyGravityToGrid = (grid: Letter[][]): Letter[][] => {
  const gridSize = grid.length;
  
  // Step 1: Create working copy
  const workingGrid: Letter[][] = [];
  for (let row = 0; row < gridSize; row++) {
    workingGrid[row] = [];
    for (let col = 0; col < gridSize; col++) {
      workingGrid[row][col] = { ...grid[row][col] };
    }
  }
  
  // Step 2: Apply gravity column by column - letters fall down to fill gaps
  for (let col = 0; col < gridSize; col++) {
    // First, collect all unused letters in this column (maintaining their original order)
    const unusedLetters: Letter[] = [];
    for (let row = 0; row < gridSize; row++) {
      if (!workingGrid[row][col].used) {
        unusedLetters.push(workingGrid[row][col]);
      }
    }
    
    // Clear the entire column first
    for (let row = 0; row < gridSize; row++) {
      workingGrid[row][col] = {
        char: ' ',
        position: { row, col },
        used: true
      };
    }
    
    // Then place unused letters at the bottom of the column (maintaining original order)
    for (let i = 0; i < unusedLetters.length; i++) {
      const targetRow = gridSize - unusedLetters.length + i; // Place from bottom up, maintaining order
      workingGrid[targetRow][col] = {
        ...unusedLetters[i],
        position: { row: targetRow, col },
        used: false
      };
    }
  }
  
  // Step 3: Keep original grid size - don't collapse empty rows
  // Empty spaces will be displayed as empty tiles (with dotted border) instead of collapsing
  // This makes it more obvious to users what happened when columns are cleared
  
  // Update positions to match the grid coordinates
  for (let row = 0; row < gridSize; row++) {
    for (let col = 0; col < gridSize; col++) {
      workingGrid[row][col].position = { row, col };
    }
  }
  
  return workingGrid;
};

// Helper function to generate random letters
const getRandomLetter = (): string => {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  return letters[Math.floor(Math.random() * letters.length)];
};

// =============================
// Procedural level generation
// =============================

const createEmptyGrid = (size: number): Letter[][] => {
  const grid: Letter[][] = [];
  for (let r = 0; r < size; r++) {
    grid[r] = [];
    for (let c = 0; c < size; c++) {
      grid[r][c] = {
        char: ' ',
        position: { row: r, col: c },
        used: false
      };
    }
  }
  return grid;
};

const directions: Array<[number, number]> = [
  [-1, -1], [-1, 0], [-1, 1],
  [0, -1], /*self*/ [0, 1],
  [1, -1], [1, 0], [1, 1]
];

const canPlaceWord = (grid: Letter[][], word: string, row: number, col: number, dr: number, dc: number): boolean => {
  const size = grid.length;
  for (let i = 0; i < word.length; i++) {
    const r = row + dr * i;
    const c = col + dc * i;
    if (r < 0 || r >= size || c < 0 || c >= size) return false;
    const ch = grid[r][c].char;
    if (ch !== ' ' && ch !== word[i]) return false;
  }
  return true;
};

const placeWord = (grid: Letter[][], word: string, row: number, col: number, dr: number, dc: number) => {
  for (let i = 0; i < word.length; i++) {
    const r = row + dr * i;
    const c = col + dc * i;
    grid[r][c] = {
      char: word[i],
      position: { row: r, col: c },
      used: false
    };
  }
};

const pickWordsForLevel = (size: number): string[] => {
  // Favor 3-6 letter words for tight grids
  const lengths = size === 3 ? [3, 4, 5] : size === 4 ? [3, 4, 5, 6] : [4, 5, 6, 7];
  const target = size === 3 ? 6 : size === 4 ? 12 : 18;
  const words: string[] = [];
  for (let i = 0; i < target; i++) {
    const len = lengths[Math.floor(Math.random() * lengths.length)];
    const pool = getWordsByLength(len);
    if (!pool || pool.length === 0) continue;
    const w = pool[Math.floor(Math.random() * pool.length)].toUpperCase();
    words.push(w);
  }
  return words;
};

const fillEmptiesWithRandomLetters = (grid: Letter[][]) => {
  const size = grid.length;
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (grid[r][c].char === ' ') {
        grid[r][c] = {
          char: getRandomLetter(),
          position: { row: r, col: c },
          used: false
        };
      }
    }
  }
};

export const generateProceduralGrid = (size: number): Letter[][] => {
  // Size is now passed directly (not calculated from levelId)
  // This allows grid size overrides to work correctly
  const minWordCount = size === 5 ? 45 : size === 6 ? 70 : 95;

  const maxAttempts = 30; // Reduced from 60 for faster fallback generation
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const grid = createEmptyGrid(size);

    // Try to place a bunch of words
    const words = pickWordsForLevel(size);
    for (const w of words) {
      // Try several random placements for this word
      let placed = false;
      for (let tries = 0; tries < 60 && !placed; tries++) {
        const [dr, dc] = directions[Math.floor(Math.random() * directions.length)];
        const startRow = Math.floor(Math.random() * size);
        const startCol = Math.floor(Math.random() * size);
        if (canPlaceWord(grid, w, startRow, startCol, dr, dc)) {
          placeWord(grid, w, startRow, startCol, dr, dc);
          placed = true;
        }
      }
    }

    // Fill the rest with random letters
    fillEmptiesWithRandomLetters(grid);

    // Skip expensive validation - just return first grid that places words
    // The worker validates grids properly, and this is just a fallback
    // Return after fewer attempts to avoid blocking
    if (attempt < 10) {
      // For first 10 attempts, do quick validation
      const all = findAllWords(grid);
      const unique = new Set(all.map(a => a.word));
      if (unique.size >= minWordCount) {
        return grid;
      }
    } else {
      // After 10 attempts, just return a grid with placed words
      // This avoids expensive validation on every attempt
      return grid;
    }
  }

  // Fallback: random grid if attempts fail
  const fallback = createEmptyGrid(size);
  fillEmptiesWithRandomLetters(fallback);
  return fallback;
};

export const gameReducer = (state: GameState, action: GameAction): GameState => {
  switch (action.type) {
    case 'SELECT_LETTER': {
      const { position } = action;
      
      // Check if the position is already selected
      const isAlreadySelected = state.selectedPath.some(
        p => p.row === position.row && p.col === position.col
      );
      
      if (isAlreadySelected) {
        // Remove the position from the selected path
        const newSelectedPath = state.selectedPath.filter(
          p => !(p.row === position.row && p.col === position.col)
        );
        const currentWord = getCurrentWord(newSelectedPath, state.grid);
        
        return {
          ...state,
          selectedPath: newSelectedPath,
          currentWord,
          feedbackMessage: '',
          hint: null
        };
      }
      
      // If not already selected, check if it can be selected
      if (!canSelectPosition(position, state.selectedPath, state.grid)) {
        return state;
      }
      
      const newSelectedPath = [...state.selectedPath, position];
      const currentWord = getCurrentWord(newSelectedPath, state.grid);
      
      return {
        ...state,
        selectedPath: newSelectedPath,
        currentWord,
        feedbackMessage: '',
        hint: null
      };
    }
    
    case 'SUBMIT_WORD': {
      if (state.selectedPath.length < 3) {
        return {
          ...state,
          feedbackMessage: 'Words must be at least 3 letters long!'
        };
      }
      
      const word = state.currentWord;
      
      if (!validateWord(word)) {
        return {
          ...state,
          selectedPath: [],
          currentWord: '',
          feedbackMessage: `"${word}" is not a valid word!`
        };
      }
      
      // Mark letters as used and apply gravity
      // OPTIMIZE: Pre-compute which positions are in selectedPath for O(1) lookup
      const selectedPathSet = new Set(state.selectedPath.map(pos => `${pos.row}-${pos.col}`));
      const gridWithUsedLetters = state.grid.map(row => 
        row.map(letter => {
          const posKey = `${letter.position.row}-${letter.position.col}`;
          return {
            ...letter,
            used: selectedPathSet.has(posKey) || letter.used
          };
        })
      );
      
      // Check if grid is completely cleared BEFORE applying gravity
      // OPTIMIZE: Use for loop instead of flat().filter() for better performance
      let unusedCount = 0;
      for (let row = 0; row < gridWithUsedLetters.length; row++) {
        for (let col = 0; col < gridWithUsedLetters[row].length; col++) {
          if (!gridWithUsedLetters[row][col].used) unusedCount++;
        }
      }
      const isGridCleared = unusedCount === 0;
      
      // Apply gravity to create new smaller grid
      const newGrid = applyGravityToGrid(gridWithUsedLetters);
      
      // Only log in debug mode to avoid blocking UI
      // Disabled verbose submit logs - they were causing perceived delay
      const VERBOSE_SUBMIT_LOGS = false;
      if (IS_DEBUG) {
        const tilesToRemove: Array<{char: string, row: number, col: number}> = [];
        const tilesRemaining: Array<{char: string, row: number, col: number}> = [];
        
        for (let row = 0; row < gridWithUsedLetters.length; row++) {
          for (let col = 0; col < gridWithUsedLetters[row].length; col++) {
            if (gridWithUsedLetters[row][col].used) {
              tilesToRemove.push({
                char: gridWithUsedLetters[row][col].char,
                row,
                col
              });
            } else {
              tilesRemaining.push({
                char: gridWithUsedLetters[row][col].char,
                row,
                col
              });
            }
          }
        }
        
        // Removed verbose logs - they were causing delay
      }
      
      const scoreBreakdown = calculateScoreBreakdown(word);
      const wordScore = scoreBreakdown.total;
      const revealedByHints = state.hintSession?.revealedCount ?? 0;
      // Strict zero-score rule: only if the submitted path exactly matches the fully revealed hint path
      const usedSessionFull = !!state.hintSession 
        && state.hintSession.revealedCount >= state.hintSession.path.length
        && state.selectedPath.length === state.hintSession.path.length
        && state.selectedPath.every((p, i) => p.row === state.hintSession!.path[i].row && p.col === state.hintSession!.path[i].col);
      const noSelfFound = usedSessionFull;
      const hintPenalty = noSelfFound ? wordScore : Math.min(revealedByHints * 10, wordScore);
      const adjustedScore = Math.max(0, wordScore - hintPenalty);
      const newFoundWords = [...state.foundWords, word];
      const gameComplete = isGameComplete(newGrid);
      
      VERBOSE_SUBMIT_LOGS && console.log(`Word submitted: "${word}" (${wordScore} points)`);
      VERBOSE_SUBMIT_LOGS && console.log(`  Breakdown: base=${scoreBreakdown.baseScore}, letters=${scoreBreakdown.letterBonus}, length=${scoreBreakdown.lengthBonus}`);
      VERBOSE_SUBMIT_LOGS && console.log(`Total words found so far: ${newFoundWords.length}`, newFoundWords);
      
      // Check for remaining words - defer expensive operation to allow animations to play
      // This check can happen in background after animations start
      const remainingTilesCount = newGrid.flat().length;
      
      // Optimize: if grid is very large and we haven't found many words, we can skip
      const shouldCheck = remainingTilesCount <= 36 || newFoundWords.length >= 4;
      
      // For now, defer the expensive check - will be handled by useEffect in App.tsx
      // that runs after animations complete
      let remainingWords: WordPath[] = [];
      let allWordsFound = false;
      
      // ALWAYS defer findAllWords - it's expensive and blocks the UI thread
      // Even for small grids, defer it to allow animations to start immediately
      remainingWords = [];
      allWordsFound = false;
      
      // The completion check will be done in a useEffect in App.tsx after animations complete
      
      VERBOSE_SUBMIT_LOGS && console.log(`Checking All Words Found Bonus:`);
      VERBOSE_SUBMIT_LOGS && console.log(`- isGridCleared: ${isGridCleared}`);
      VERBOSE_SUBMIT_LOGS && console.log(`- remainingWords.length: ${remainingWords.length}`);
      VERBOSE_SUBMIT_LOGS && console.log(`- allWordsFound: ${allWordsFound}`);
      
      // Simple feedback message without breakdown
      let feedbackMessage = `Great! "${word}" (+${adjustedScore} points)`;
      if (adjustedScore < wordScore) {
        feedbackMessage = `Great! "${word}" (+${adjustedScore} points, hint used)`;
      }
      let finalScore = state.score + adjustedScore;
      let completionBonus = 0;
      let tileClearingBonus = 0;
      let allWordsFoundBonus = 0;
      
      // Level is complete ONLY if grid is cleared
      // We defer the remainingWords check to avoid blocking the UI thread
      // The remainingWords array is intentionally left empty here to defer the expensive check
      // Completion based on remaining words should be checked separately after animations
      const isComplete = isGridCleared;
      
      // Removed completion log - was causing delay
      
      if (isComplete) {
        VERBOSE_SUBMIT_LOGS && console.log(`Level completion detected!`);
        VERBOSE_SUBMIT_LOGS && console.log(`- Grid cleared: ${isGridCleared}`);
        VERBOSE_SUBMIT_LOGS && console.log(`- Remaining words: ${remainingWords.length}`);
        VERBOSE_SUBMIT_LOGS && console.log(`- Words found: ${newFoundWords.length}`);
        VERBOSE_SUBMIT_LOGS && console.log(`- Found words:`, newFoundWords);
        
        // Base completion bonus
        completionBonus = Math.max(100, newFoundWords.length * 10);
        
        // Additional bonus for clearing all tiles
        if (isGridCleared) {
          tileClearingBonus = 200; // Big bonus for clearing all tiles
          VERBOSE_SUBMIT_LOGS && console.log(`Tile clearing bonus awarded: ${tileClearingBonus} points`);
        }
        
        // Bonus for finding all possible words AND clearing all tiles
        if (isGridCleared && remainingWords.length === 0) {
          allWordsFoundBonus = newFoundWords.length * 25;
          VERBOSE_SUBMIT_LOGS && console.log(`All words found bonus awarded: ${allWordsFoundBonus} points (all tiles used + all words found)`);
        } else if (remainingWords.length === 0) {
          VERBOSE_SUBMIT_LOGS && console.log(`All words found but NOT all tiles used - no all-words bonus`);
        }
        
        finalScore += completionBonus + tileClearingBonus + allWordsFoundBonus;
        
        let bonusText = `+${completionBonus} completion bonus`;
        if (tileClearingBonus > 0) {
          bonusText += ` +${tileClearingBonus} tile clearing bonus`;
        }
        if (allWordsFoundBonus > 0) {
          bonusText += ` +${allWordsFoundBonus} all words found bonus`;
        }
        
        feedbackMessage = `Level Complete! Found ${newFoundWords.length} words! (${bonusText})`;
      }
      return {
        ...state,
        grid: newGrid,
        selectedPath: [],
        currentWord: '',
        score: finalScore,
        foundWords: newFoundWords,
        gameComplete,
        allWordsFound,
        feedbackMessage,
        tileClearingBonus: tileClearingBonus > 0 ? tileClearingBonus : undefined,
        hint: null,
        hintSession: null
      };
    }
    
    case 'CLEAR_SELECTION': {
      return {
        ...state,
        selectedPath: [],
        currentWord: '',
        feedbackMessage: '',
        hint: null
      };
    }
    
    case 'NEW_GAME': {
      const { config } = action;
      const levelToStart = config?.level || 1; // Use level from config if provided, otherwise default to 1
      const gridSizeOverride = config?.gridSize; // Use gridSize from config if provided (for debug mode)
      const newGrid = generateGrid(levelToStart, gridSizeOverride);
      
      return {
        grid: newGrid,
        selectedPath: [],
        score: 0,
        foundWords: [],
        gameComplete: false,
        currentWord: '',
        feedbackMessage: '',
        level: levelToStart,
        allWordsFound: false,
        hint: null,
        hintSession: null
      };
    }
    
    case 'NEXT_LEVEL': {
      const nextLevel = state.level + 1;
      const newGrid = action.preGeneratedGrid || generateGrid(nextLevel);
      
      return {
        ...state,
        grid: newGrid,
        selectedPath: [],
        foundWords: [], // Reset found words for new level
        gameComplete: false,
        currentWord: '',
        feedbackMessage: `Level ${nextLevel}! New puzzle ready!`,
        level: nextLevel,
        allWordsFound: false,
        // Keep the score from previous level
        score: state.score,
        hint: null,
        hintSession: null
      };
    }

    case 'REQUEST_HINT_TIER1': {
      // Find any remaining word and hint its starting position
      const all = findAllWords(state.grid);
      const remaining = all.filter(w => !state.foundWords.includes(w.word));
      if (remaining.length === 0) {
        return { ...state, feedbackMessage: 'No hints available.' };
      }
      // Prefer shorter/easier word
      const candidate = remaining.sort((a, b) => a.word.length - b.word.length)[0];
      const firstPos = candidate.path[0];
      return {
        ...state,
        hint: { type: 'firstLetter', position: firstPos },
        feedbackMessage: 'Hint: try this starting tile.'
      };
    }

    case 'REQUEST_HINT_TIER2': {
      const all = findAllWords(state.grid);
      const remaining = all.filter(w => !state.foundWords.includes(w.word));
      if (remaining.length === 0) {
        return { ...state, feedbackMessage: 'No hints available.' };
      }
      // If there is a current selection, try to continue that path
      if (state.selectedPath.length > 0) {
        const len = state.selectedPath.length;
        const match = remaining.find(w => {
          if (w.path.length <= len) return false;
          for (let i = 0; i < len; i++) {
            if (w.path[i].row !== state.selectedPath[i].row || w.path[i].col !== state.selectedPath[i].col) return false;
          }
          return true;
        });
        if (match) {
          const nextPos = match.path[len];
          return { ...state, hint: { type: 'nextStep', position: nextPos }, feedbackMessage: 'Hint: try this next tile.' };
        }
      }
      // Fallback: show a start tile like tier 1
      const candidate = remaining.sort((a, b) => a.word.length - b.word.length)[0];
      return { ...state, hint: { type: 'firstLetter', position: candidate.path[0] }, feedbackMessage: 'Hint: try this starting tile.' };
    }

    case 'CLEAR_HINT': {
      return { ...state, hint: null, hintSession: null };
    }

    case 'REQUEST_HINT_PROGRESSIVE': {
      const selLen = state.selectedPath.length;
      let session = state.hintSession;
      let targetPath: Position[] | null = null;

      // OPTIMIZATION: If we have an existing session that we can continue, skip expensive findAllWords
      // This makes hint clicking instant when continuing a session
      if (session && session.revealedCount < session.path.length) {
        // Continue existing session - no need to recalculate words
        targetPath = session.path;
        const nextIndex = Math.min(session.revealedCount, targetPath.length - 1);
        const nextPos = targetPath[nextIndex];
        const newRevealed = Math.min(targetPath.length, nextIndex + 1);
        const hintType: 'firstLetter' | 'nextStep' = nextIndex === 0 ? 'firstLetter' : 'nextStep';

        const result = {
          ...state,
          hint: { type: hintType, position: nextPos },
          hintSession: { path: targetPath, revealedCount: newRevealed },
          feedbackMessage: nextIndex === 0 ? 'Hint: try this starting tile.' : 'Hint: try this next tile.'
        };
        
        return result;
      }

      // If a session exists and it's already fully revealed, do not advance further
      if (session && session.revealedCount >= session.path.length) {
        return {
          ...state,
          hint: { type: 'nextStep', position: session.path[session.path.length - 1] },
          feedbackMessage: 'Hint: word fully revealed. Submit to score 0.'
        };
      }

      // Fast path: Find just the first few words (much faster than findAllWords)
      // Only do full search if we need to match a selection and didn't find a match
      const foundWordsSet = new Set(state.foundWords);
      let remaining: WordPath[] = [];
      
      // If we have a selection, try to find a matching word quickly
      if (selLen > 0) {
        // First, try to find a few words that might match the selection
        // We'll search for up to 10 words to increase chance of finding a match
        const candidateWords = findFirstWords(state.grid, 10, foundWordsSet);
        const match = candidateWords.find(w => {
          if (w.path.length <= selLen) return false;
          if (selLen === 1) {
            return w.path[0].row === state.selectedPath[0].row && w.path[0].col === state.selectedPath[0].col;
          }
          for (let i = 0; i < selLen; i++) {
            if (w.path[i].row !== state.selectedPath[i].row || w.path[i].col !== state.selectedPath[i].col) return false;
          }
          return true;
        });
        
        if (match) {
          // Found a match! Use it immediately
          targetPath = match.path;
          session = { path: match.path, revealedCount: Math.max(session?.revealedCount ?? 0, Math.min(selLen, match.path.length - 1)) };
        } else {
          // No match found in quick search - need to do full search
          // This is rare, only happens when selection doesn't match any of the first 10 words
          const all = findAllWords(state.grid);
          remaining = all.filter(w => !foundWordsSet.has(w.word));
          const fullMatch = remaining.find(w => {
            if (w.path.length <= selLen) return false;
            if (selLen === 1) {
              return w.path[0].row === state.selectedPath[0].row && w.path[0].col === state.selectedPath[0].col;
            }
            for (let i = 0; i < selLen; i++) {
              if (w.path[i].row !== state.selectedPath[i].row || w.path[i].col !== state.selectedPath[i].col) return false;
            }
            return true;
          });
          if (fullMatch) {
            targetPath = fullMatch.path;
            session = { path: fullMatch.path, revealedCount: Math.max(session?.revealedCount ?? 0, Math.min(selLen, fullMatch.path.length - 1)) };
          }
        }
      }
      
      // If no selection-based match, use fast search to get first word
      if (!targetPath) {
        const candidateWords = findFirstWords(state.grid, 1, foundWordsSet);
        if (candidateWords.length > 0) {
          targetPath = candidateWords[0].path;
          session = { path: targetPath, revealedCount: 0 };
        } else {
          // No words found - check if really no words available
          remaining = remaining.length > 0 ? remaining : findAllWords(state.grid).filter(w => !foundWordsSet.has(w.word));
          if (remaining.length === 0) {
            return { ...state, feedbackMessage: 'No hints available.' };
          }
          const candidate = remaining.sort((a, b) => a.word.length - b.word.length)[0];
          targetPath = candidate.path;
          session = { path: targetPath, revealedCount: 0 };
        }
      }
      
      if (!targetPath) {
        return { ...state, feedbackMessage: 'No hints available.' };
      }

      const nextIndex = Math.min(session!.revealedCount, targetPath.length - 1);
      const nextPos = targetPath[nextIndex];
      const newRevealed = Math.min(targetPath.length, nextIndex + 1);
      const hintType: 'firstLetter' | 'nextStep' = nextIndex === 0 ? 'firstLetter' : 'nextStep';

      const result = {
        ...state,
        hint: { type: hintType, position: nextPos },
        hintSession: { path: targetPath, revealedCount: newRevealed },
        feedbackMessage: nextIndex === 0 ? 'Hint: try this starting tile.' : 'Hint: try this next tile.'
      };
      
      return result;
    }
    
    default:
      return state;
  }
};
